/**
 * Wire types from xop-marketing-app's HTTP API. Kept local to avoid a hard
 * cross-repo dep — the contract is the wire shape.
 */
export type Persona = "support" | "marketing" | "content";

export const PLATFORMS = [
  "linkedin_post",
  "twitter_thread",
  "threads_post",
  "blog_post",
  "press_release",
  "video_script",
  "youtube_short_script",
  "reddit_post",
  "facebook_post",
  "instagram_caption",
  "email",
] as const;
export type Platform = (typeof PLATFORMS)[number];

export const PLATFORM_LABELS: Record<Platform, string> = {
  linkedin_post: "LinkedIn",
  twitter_thread: "X thread",
  threads_post: "Threads",
  blog_post: "Blog",
  press_release: "Press release",
  video_script: "Video script",
  youtube_short_script: "YouTube Short",
  reddit_post: "Reddit",
  facebook_post: "Facebook",
  instagram_caption: "Instagram",
  email: "Email",
};

/**
 * Platforms the operator can CREATE content for. The four social channels are
 * also the agent's AUTONOMOUS set; press releases + blog posts are LONG-FORM
 * operator-creatable extras (Create New / generate-from-idea), re-added
 * 2026-06-16 — they are NOT in the marketing app's `generation_platforms`, so
 * the always-on loop never sprays long-form. The still-retired channels
 * (video / YouTube / Reddit / Instagram / email) stay in `PLATFORMS` only so
 * any legacy draft still renders, but are never offered for new work. Keep in
 * lockstep with the marketing app's `generation_platforms` + the creatable
 * extras (press_release, blog_post) and `PLATFORM_TEMPLATES`.
 */
export const CREATABLE_PLATFORMS = [
  "linkedin_post",
  "twitter_thread",
  "threads_post",
  "facebook_post",
  "blog_post",
  "press_release",
] as const satisfies readonly Platform[];

/** Platforms that get a SMALL compact card (vs the full landscape card). */
export const COMPACT_IMAGE_PLATFORMS: readonly Platform[] = [
  "twitter_thread",
  "threads_post",
];

export const DRAFT_STATUSES = [
  "draft",
  "in_review",
  "approved",
  "rejected",
  "published_externally",
] as const;
export type DraftStatus = (typeof DRAFT_STATUSES)[number];

export const AGENT_NAMES = [
  "trend_scan",
  "sales_sync",
  "content_gen",
  "perf_tracker",
  "company_research",
  "press_release_detector",
  "scheduler",
  "publisher",
] as const;
export type AgentName = (typeof AGENT_NAMES)[number];

export interface DraftSource {
  index: number;
  title: string | null;
  url: string | null;
  source_app: string;
  source_type: string;
  source_id: string;
  distance: number;
}

export interface Draft {
  id: number;
  trend_id: number | null;
  sales_signal_id: number | null;
  campaign_id: number | null;
  press_trigger_id: number | null;
  platform: Platform;
  title: string | null;
  body: string;
  image_prompt: string | null;
  image_b64: string | null;
  image_model: string | null;
  metadata: Record<string, unknown>;
  status: DraftStatus;
  generated_by: "agent" | "human";
  generation_persona: Persona | null;
  generation_sources: DraftSource[];
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  version: number;
  suggested_post_at: string | null;
  auto_publishable: boolean;
  priority_score: number;
  thinking_summary: string | null;
  created_at: string;
  updated_at: string;
}

export interface DraftReviewAction {
  action: "approve" | "reject" | "edit" | "regenerate";
  notes?: string | null;
  body?: string | null;
  title?: string | null;
  image_prompt?: string | null;
  reviewer?: string | null;
  auto_publishable?: boolean | null;
  suggested_post_at?: string | null;
}

export interface Trend {
  id: number;
  topic: string;
  source: "rss" | "sales_signal" | "kb" | "manual";
  source_ref: string | null;
  summary: string | null;
  raw_data: Record<string, unknown>;
  score: number;
  num_mentions: number;
  found_at: string;
  expires_at: string;
  status: "active" | "expired" | "suppressed";
}

export interface Campaign {
  id: number;
  name: string;
  goal: string | null;
  target_persona: string | null;
  start_date: string | null;
  end_date: string | null;
  status: "planned" | "live" | "paused" | "done";
  metrics: Record<string, unknown>;
}

export interface AgentRun {
  id: number;
  agent_name: AgentName;
  status: "running" | "ok" | "failed";
  started_at: string;
  finished_at: string | null;
  output_summary: Record<string, unknown>;
  error: string | null;
}

export interface AgentStatusRow {
  name: AgentName;
  enabled: boolean;
  next_scheduled_at: string | null;
  last_run: AgentRun | null;
}

export interface MarketingMetricsOverview {
  // NOTE: `campaigns_live` was removed (2026-05-20, security finding S7).
  // The dashboard hero has no "Live campaigns" tile — see
  // docs/dashboard-pulse.md. Don't re-add it without a
  // real surface to drive from it; `types.test.ts` pins its absence.
  drafts: number;
  avg_open_rate_pct: number | null;
  scheduled_posts: number;
  trends_active: number;
  drafts_approved_7d: number;
  drafts_rejected_7d: number;
  agent_health: "healthy" | "degraded" | "down";
  knowledge_sources: number;
  press_triggers_pending: number;
  published_7d: number;
  safe_mode_7d: number;
}

export type ThoughtType =
  | "plan"
  | "observe"
  | "search"
  | "decide"
  | "generate"
  | "critique"
  | "finalize"
  | "error";

export interface AgentThought {
  id: number;
  agent_run_id: number | null;
  agent_name: string;
  thought_type: ThoughtType;
  title: string;
  content: string | null;
  payload: Record<string, unknown>;
  draft_id: number | null;
  trend_id: number | null;
  press_trigger_id: number | null;
  duration_ms: number | null;
  created_at: string;
}

export type KnowledgeKind =
  | "kb_doc"
  | "external_url"
  | "website_section"
  | "product_fact"
  | "customer_win"
  | "release_note"
  | "sales_objection";

export interface KnowledgeSource {
  id: number;
  name: string;
  kind: KnowledgeKind;
  source_url: string | null;
  source_app: string | null;
  source_type: string | null;
  source_id: string | null;
  summary: string | null;
  key_facts: string[];
  importance: number;
  last_indexed_at: string;
  metadata: Record<string, unknown>;
  status: string;
}

export type TriggerKind =
  | "release_launch"
  | "customer_win"
  | "milestone"
  | "partnership"
  | "award"
  | "leadership_announcement"
  | "product_update";

export type TriggerStatus = "detected" | "drafted" | "dismissed" | "published";

export interface PressReleaseTrigger {
  id: number;
  trigger_kind: TriggerKind;
  signal_source: string;
  headline: string;
  summary: string | null;
  context: Record<string, unknown>;
  newsworthiness: number;
  status: TriggerStatus;
  draft_id: number | null;
  detected_at: string;
  resolved_at: string | null;
  notes: string | null;
}

export type ScheduledStatus =
  | "queued"
  | "publishing"
  | "published"
  | "failed"
  | "canceled"
  | "skipped"
  | "safe_mode";

export interface ScheduledPost {
  id: number;
  draft_id: number;
  platform: Platform;
  account_id: number | null;
  scheduled_for: string;
  status: ScheduledStatus;
  scheduled_by: "agent" | "human";
  attempts: number;
  max_attempts: number;
  last_error: string | null;
  external_post_id: string | null;
  external_url: string | null;
  published_at: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PublishAttempt {
  id: number;
  scheduled_post_id: number;
  attempt: number;
  status: string;
  started_at: string;
  finished_at: string | null;
  request_payload: Record<string, unknown>;
  response_payload: Record<string, unknown>;
  error: string | null;
}

export interface PlatformAccount {
  id: number;
  platform: Platform;
  account_name: string;
  display_name: string | null;
  status: "connected" | "disconnected" | "error" | "safe_mode";
  credentials_ref: string | null;
  // Non-secret last-4 hint + when the token was set. The token itself is
  // write-only and never present on this type.
  credential_hint: string | null;
  credential_set_at: string | null;
  publish_enabled: boolean;
  last_check_at: string | null;
  last_check_error: string | null;
  metadata: Record<string, unknown>;
}

export type Autonomy = "review_gated" | "fully_auto" | "manual";

export interface AgentSettings {
  paused: boolean;
  steer_prompt: string | null;
  autonomy: Autonomy;
  publisher_enabled: boolean;
  // Master switch for autonomous drafting (default off — the agent suggests
  // ideas; the operator drives generation).
  auto_draft_enabled: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

export interface AgentSettingsUpdate {
  paused?: boolean | null;
  steer_prompt?: string | null;
  clear_steer?: boolean;
  autonomy?: Autonomy | null;
  publisher_enabled?: boolean | null;
  auto_draft_enabled?: boolean | null;
}

// ── Create New (compose from a free-text brief) ─────────────────────
export interface ComposeRequest {
  brief: string;
  platform: Platform;
  title?: string | null;
  angle?: string | null;
}

// ── Ideas (AI-suggested topics) ─────────────────────────────────────
export interface Idea {
  id: number;
  topic: string;
  summary: string | null;
  source: string;
  source_ref: string | null;
  score: number;
  num_mentions: number;
  content_angle: string;
  reason: string;
  found_at: string;
  has_draft: boolean;
}

// ── Budget / economics ──────────────────────────────────────────────
export interface BudgetState {
  spend_usd: number;
  budget_usd: number;
  exhausted: boolean;
  window_start: string;
  resets_at: string;
}

export interface Economics {
  budget: BudgetState;
  since: string;
  total_cost_usd: number;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  by_caller: Record<string, number>;
  by_model: Record<string, number>;
  // The actual chat model the gateway is billing marketing for this window
  // (e.g. "claude-sonnet-4-6"); null until the first completion reports usage.
  primary_model?: string | null;
  estimated: boolean;
}

// ── Capabilities ("what the agent knows / can do") ──────────────────
export interface CapabilitySkill {
  name: string;
  category: string;
  description: string;
}
export interface CapabilityDataAccess {
  source: string;
  scope: string;
}
export interface KnowledgeStanding {
  sources: number;
  pdf_sources: number;
  last_indexed_at: string | null;
  top_sources: string[];
}
export interface Capabilities {
  platforms: string[];
  skills: CapabilitySkill[];
  data_access: CapabilityDataAccess[];
  cannot: string[];
  brand_voice: string;
  target_persona: string;
  knowledge: KnowledgeStanding;
  budget: BudgetState;
}

// ── Knowledge base readability probe ────────────────────────────────
export interface KnowledgeHit {
  title: string;
  url: string | null;
  source_app: string | null;
  source_type: string | null;
  is_pdf: boolean;
  distance: number | null;
}
export interface KnowledgeHealth {
  reachable: boolean;
  query: string;
  hits: number;
  distinct_docs: number;
  pdf_docs: number;
  samples: KnowledgeHit[];
  detail: string | null;
  checked_at: string;
}
